import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  changeDiseasesAction,
  changeDiseasesPageSizeAction,
  changeSelectedSymptomsAction,
  changeSymptomsAction,
  changeTotalDiseasesAction,
} from "../../../reducers/searchOmimState";
import { useIntl } from "react-intl";
import { getDiseases, getSymptoms } from "../../../services/commonservices";
import { IconButton, Typography } from "@material-ui/core";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import AlertDialog from "../AlertDialog";
import Grid from "@material-ui/core/Grid";
import {
  DISEASES_PAGE_SIZE,
  SYMPTOMS_PAGE_SIZE,
  SYMPTOMS_PAGE_SIZE_LARGE,
  TABLE_HEIGHT,
} from "../../../utils/constants";
import PaginationHandlerComponent from "../PaginationHandlerComponent";
import { formatCategoriesList } from "../../../utils/utils";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "100%",
    display: "grid",
    gap: "0.5rem",
  },
  selectSymptoms: {
    borderBottom: `2px solid ${theme.palette.secondary.main}`,
  },
  accordionBody: {
    boxShadow: "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  symptomsList: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    justifyContent: "flex-start",
    alignItems: "center",
  },
  symptomListFull: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    overflow: "scroll",
  },
  symptom: {
    cursor: "pointer",
    borderRadius: 30,
    padding: theme.spacing(1, 2),
    width: "100%",
    backgroundColor: "#F3F2FC",
    border: "1px solid #FFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  symptomSelected: {
    border: "1px solid #FF3185",
    backgroundColor: "#FFF6FA",
  },
  symptomsContainer: {
    display: "grid",
    overflow: "scroll",
    gap: theme.spacing(1),
    paddingRight: "1rem",
    height: TABLE_HEIGHT,
  },
  symptomBlock: {
    display: "flex",
    width: "100%",
  },
  divider: {
    backgroundColor: theme.palette.secondary.main,
  },
  modalTitle: {
    padding: theme.spacing(3),
    color: theme.palette.secondary.main,
    wordBreak: "break-word",
  },
  modalButton: {
    margin: theme.spacing(2),
    border: "1px solid #FFF",
    backgroundColor: "#F3F2FC",
    borderRadius: 30,
    cursor: "pointer",
    padding: theme.spacing(1, 3),
    color: theme.palette.text.main + "!important",
    textTransform: "capitalize",
    "&:hover": {
      border: "1px solid #FF3185",
      backgroundColor: "#FFF6FA",
    },
  },
  synonymTitle: {
    color: "black",
    marginBottom: theme.spacing(2),
  },
  synonym: {
    color: "black",
  },
}));

const SymptomsComponent = ({ displaySelected = true }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();

  const [modalObject, setModalObject] = useState({ open: false, symptom: null });
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = React.useState(false);

  const loading = useSelector((state) => state.common.loading);
  const symptoms = useSelector((state) => state.searchOmim.symptoms);
  const selectedSymptoms = useSelector((state) => state.searchOmim.selectedSymptoms);
  const searchText = useSelector((state) => state.searchOmim.searchText);
  const categoriesSelected = useSelector((state) => state.searchOmim.selectedCategories);
  const totalSymptoms = useSelector((state) => state.searchOmim.totalSymptoms);

  const pageSize = displaySelected ? SYMPTOMS_PAGE_SIZE : SYMPTOMS_PAGE_SIZE_LARGE;

  const lastPage = Math.ceil(totalSymptoms / pageSize);

  const handleSetExpanded = () => {
    setExpanded((expanded) => !expanded);
  };

  const isSymptomSelected = (symptom) => {
    return selectedSymptoms.find((item) => item.id === symptom.id) || false;
  };

  const handleDiseasesSuccess = (response) => {
    dispatch(changeDiseasesPageSizeAction(parseInt(response.headers["pagination-page-size"])));
    dispatch(changeTotalDiseasesAction(parseInt(response.headers["pagination-total-results"])));
    dispatch(changeDiseasesAction(response.data));
  };

  const handleModalObject = (symptom) => {
    setModalObject({ open: true, symptom: symptoms.find((item) => item.id === symptom) });
  };

  const handleSymptomOnClick = (symptom) => {
    const exist = isSymptomSelected(symptom);
    let newSymptoms;
    if (exist) {
      newSymptoms = selectedSymptoms.filter((item) => item !== symptom);
    } else {
      newSymptoms = [...selectedSymptoms];
      newSymptoms.push(symptom);
    }

    dispatch(changeSelectedSymptomsAction(newSymptoms));

    const formattedSymptoms = newSymptoms.map((s) => s.id);

    displaySelected && getDiseases(null, formattedSymptoms, null, DISEASES_PAGE_SIZE, 0, handleDiseasesSuccess);
  };

  const handleModalClose = () => {
    setModalObject({ open: false, symptom: null });
  };

  const handleSearchSuccess = (response) => {
    dispatch(changeSymptomsAction(response.data));
  };

  const searchSymptoms = (value) => {
    const newPage = !!value ? page + 1 : page - 1;

    setPage(newPage);
    const categories = formatCategoriesList(categoriesSelected);
    const offset = pageSize * (newPage - 1);

    getSymptoms(searchText, categories, pageSize, offset, handleSearchSuccess);
  };

  useEffect(() => {
    if (expanded && !selectedSymptoms?.length) {
      setExpanded(false);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymptoms]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesSelected]);

  return (
    <>
      <AlertDialog
        open={!!modalObject.open}
        buttonCloseText={intl.formatMessage({ id: "global_buttons_close" })}
        description={
          <>
            <Typography variant={"h5"} className={classes.synonymTitle}>
              {intl.formatMessage({ id: "searchOmimComponent.synonyms" })}
            </Typography>
            <Grid container spacing={4}>
              {modalObject?.symptom?.synonyms?.map((synonym) => (
                <Grid item sm={12} md={3} key={synonym}>
                  <Typography className={classes.synonym}>{synonym}</Typography>
                </Grid>
              ))}
            </Grid>
          </>
        }
        title={
          <Typography variant={"h4"} className={classes.modalTitle}>
            {modalObject?.symptom?.name}
          </Typography>
        }
        onClose={handleModalClose}
        buttonClassName={classes.modalButton}
      />
      <Typography variant={"h2"}>{intl.formatMessage({ id: "symptoms_and_signs" })}</Typography>
      <div className={classes.symptomsContainer}>
        {displaySelected && !!symptoms?.length && (
          <>
            <Accordion elevation={0} expanded={expanded} onChange={handleSetExpanded}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                disabled={!selectedSymptoms?.length}
                className={classes.selectSymptoms}
              >
                <Typography className={classes.heading}>
                  {intl.formatMessage({ id: "selected_symptoms_and_signs" })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionBody}>
                {selectedSymptoms?.map((symptom) => (
                  <div key={symptom.id} className={classes.symptomBlock}>
                    <div
                      key={symptom.id}
                      onClick={() => handleSymptomOnClick(symptom)}
                      className={
                        isSymptomSelected(symptom) ? `${classes.symptomSelected} ${classes.symptom}` : classes.symptom
                      }
                    >
                      <Tooltip title={symptom.name}>
                        <Typography>{symptom.name}</Typography>
                      </Tooltip>
                    </div>
                    <IconButton onClick={() => handleModalObject(symptom.id)}>
                      <VisibilityOutlinedIcon color={"secondary"} />
                    </IconButton>
                  </div>
                ))}
              </AccordionDetails>
            </Accordion>
          </>
        )}
        <>
          {displaySelected && !!symptoms?.length && (
            <Typography className={classes.heading}>{intl.formatMessage({ id: "results" }) + ":"}</Typography>
          )}
          {loading && <CircularProgress color={"secondary"} />}
          {symptoms?.map((symptom) => (
            <div key={symptom.id} className={classes.symptomBlock}>
              <div
                key={symptom.id}
                onClick={() => handleSymptomOnClick(symptom)}
                className={
                  isSymptomSelected(symptom) ? `${classes.symptomSelected} ${classes.symptom}` : classes.symptom
                }
              >
                <Tooltip title={symptom.name}>
                  <Typography>{symptom.name}</Typography>
                </Tooltip>
              </div>
              <IconButton onClick={() => handleModalObject(symptom.id)}>
                <VisibilityOutlinedIcon color={"secondary"} />
              </IconButton>
            </div>
          ))}
        </>
      </div>
      {!!symptoms?.length && (
        <PaginationHandlerComponent
          handleClick={searchSymptoms}
          disablePrevious={!!(!symptoms?.length || page === 1)}
          disableNext={!!(!symptoms?.length || (!!page && !!lastPage && page === lastPage) || !lastPage)}
          page={page}
          lastPage={lastPage}
        />
      )}
    </>
  );
};

export default SymptomsComponent;
